import React, {useState} from 'react';
import {View} from 'react-native';
import {Icon} from '@rn-vui/themed';

import {useModalDimensions} from '../../../hooks/useModalDimensions';
import {Address} from '../../../models/mfl';
import Text from '../../themed/Text';
import CrossPlatformModal from '../CrossPlatformModal';
import PressableWithOpacityFeedback from '../PressableWithOpacityFeedback';

import AddressPicker from './AddressPicker';
import ManualAddressEntry from './ManualAddressEntry';

// NOTE that the onChange return is different depending on if we are manual or not
// console log the object to see; we should probably unify it to be a proper Address
// all the time, if that's safe depending on usages

type Props = {
  address?: Address;
  onChange: (address: Address) => void;
  placeholder?: string;
  disabled?: boolean;
  testID?: string;
  /** When true, renders ManualAddressEntry inline instead of in a modal (use when already inside a modal) */
  inline?: boolean;
  /** When provided (inline mode), ManualAddressEntry's button will call this directly to save+close */
  onSubmitAndClose?: (address: Address) => void;
  /** Called when entering/exiting manual entry mode (for parent to show/hide its own buttons) */
  onModeChange?: (isManualEntry: boolean) => void;
  /**
   * DIS-1592: pre-select the suburb in manual entry when the caller already collected
   * it. Forwarded to ManualAddressEntry; street lines still start empty. Omit to keep
   * a fresh form.
   */
  initialSuburb?: Pick<Address, 'locality' | 'postal_code' | 'administrative_area'>;
};
const AddressEntry = ({
  address,
  onChange,
  placeholder,
  disabled,
  testID,
  inline,
  onSubmitAndClose,
  onModeChange,
  initialSuburb,
}: Props) => {
  const [isManualEntry, setIsManualEntry] = useState(false);
  const {containerStyle: modalContainerStyle} = useModalDimensions({
    size: 'medium',
    constrainHeight: true,
  });

  const handleManualEntryChange = (enteredAddress: Address) => {
    onChange(enteredAddress);
    setIsManualEntry(false);
    onModeChange?.(false);
  };
  const handleManualEntryCancel = () => {
    setIsManualEntry(false);
    onModeChange?.(false);
  };

  const handleSelectManualEntryDropdownOption = () => {
    setIsManualEntry(true);
    onModeChange?.(true);
  };

  // Inline mode: render ManualAddressEntry directly without modal or header
  // (parent component is responsible for modal chrome when inline=true)
  if (inline && isManualEntry) {
    // When onSubmitAndClose is provided, ManualAddressEntry's button directly saves+closes
    // Must reset state before closing so component is in correct mode when modal reopens
    const handleInlineSubmit = onSubmitAndClose
      ? (enteredAddress: Address) => {
          setIsManualEntry(false);
          onModeChange?.(false);
          onSubmitAndClose(enteredAddress);
        }
      : handleManualEntryChange;

    return (
      <ManualAddressEntry
        onChange={handleInlineSubmit}
        testID={testID ? `${testID}-manual` : undefined}
        submitButtonText={onSubmitAndClose ? 'SAVE ADDRESS' : 'Use this address'}
        initialSuburb={initialSuburb}
      />
    );
  }

  // Modal mode: wrap ManualAddressEntry in a CrossPlatformModal (standalone usage)
  if (isManualEntry) {
    return (
      <CrossPlatformModal
        isVisible={isManualEntry}
        hasBackdrop
        backdropOpacity={0.75}
        onBackdropPress={handleManualEntryCancel}
        onBackButtonPress={handleManualEntryCancel}
        keyboardAwareContent
        style={{flex: 1, justifyContent: 'center', alignItems: 'center', margin: 0}}>
        <View
          style={{
            ...modalContainerStyle,
            backgroundColor: 'white',
            padding: 20,
            borderRadius: 4,
          }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}>
            <Text variant="heading1">{placeholder || 'Enter address'}</Text>
            <PressableWithOpacityFeedback onPress={handleManualEntryCancel}>
              <Icon name="close" size={30} />
            </PressableWithOpacityFeedback>
          </View>
          <ManualAddressEntry
            onChange={handleManualEntryChange}
            testID={testID ? `${testID}-manual` : undefined}
            initialSuburb={initialSuburb}
          />
        </View>
      </CrossPlatformModal>
    );
  }

  return (
    <AddressPicker
      address={address}
      onSelect={onChange}
      onManualAddressEntry={handleSelectManualEntryDropdownOption}
      placeholder={placeholder}
      disabled={disabled}
      testID={testID ? `${testID}-picker` : undefined}
    />
  );
};

export default AddressEntry;
